/**
 * SocialMediaPage — YouTube + Instagram accounts and content metrics.
 *
 * Two tabs:
 *   - Accounts : latest follower / subscriber count per account
 *   - Content  : per-content view stats (latest sm_metric snapshot)
 *
 * Both tabs use DataGrid for sort / filter / search / CSV export, and
 * share a platform filter dropdown (all / youtube / instagram).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { socialApi, refreshCache } from '../services/mrdsApi';
import {
  PageHeader,
  MetricCard,
  SectionCard,
  EmptyState,
  PlatformChip,
  DataTable,
  CompareChart,
} from '../components/ui';
import { PLATFORM_COLORS } from '../theme';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));

const PLATFORMS = ['all', 'youtube', 'instagram'];

export default function SocialMediaPage() {
  const [tab, setTab] = useState('accounts');
  const [platform, setPlatform] = useState('all');

  const [followers, setFollowers] = useState({ rows: [], asOfDate: null, warning: null });
  const [followersLoading, setFollowersLoading] = useState(false);

  const [metrics, setMetrics] = useState({ rows: [], asOfDate: null, warning: null });
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // Per-tab compare selections
  const [accountCompareIds, setAccountCompareIds] = useState([]);
  const [contentCompareIds, setContentCompareIds] = useState([]);
  const COMPARE_MAX = 10;

  useEffect(() => {
    let cancelled = false;
    setFollowersLoading(true);
    setError('');
    socialApi
      .getFollowers({
        platform: platform === 'all' ? null : platform,
        maxAgeDays: 7,
      })
      .then((res) => {
        if (cancelled) return;
        setFollowers(res);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Hesap verileri yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setFollowersLoading(false));
    return () => {
      cancelled = true;
    };
  }, [platform, reloadKey]);

  useEffect(() => {
    if (tab !== 'content') return undefined;
    let cancelled = false;
    setMetricsLoading(true);
    socialApi
      .getContentMetrics({
        platform: platform === 'all' ? null : platform,
        maxAgeDays: 7,
      })
      .then((res) => {
        if (cancelled) return;
        setMetrics(res);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('İçerik metrikleri yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setMetricsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, platform, reloadKey]);

  const totals = useMemo(() => {
    return (followers.rows || []).reduce(
      (acc, r) => ({
        accounts: acc.accounts + 1,
        followers: acc.followers + r.follower_count,
      }),
      { accounts: 0, followers: 0 },
    );
  }, [followers]);

  const contentTotals = useMemo(() => {
    return (metrics.rows || []).reduce(
      (acc, r) => ({
        contents: acc.contents + 1,
        views: acc.views + r.views,
        likes: acc.likes + r.likes,
        comments: acc.comments + r.comments,
      }),
      { contents: 0, views: 0, likes: 0, comments: 0 },
    );
  }, [metrics]);

  // ---- Accounts grid ---------------------------------------------------
  // MUI DataGrid her satirda benzersiz bir `id` ister. MRDS API'si
  // /followers icin ayri bir kimlik alani dondurmuyor; bir hesap
  // (account, platform) ciftiyle benzersiz oldugu icin kimligi bundan
  // uretiyoruz.
  const accountRows = useMemo(
    () =>
      (followers.rows || []).map((r) => ({
        id: `${r.account}|${r.platform}`,
        account: r.account,
        platform: r.platform,
        follower_count: r.follower_count,
        report_date: r.report_date,
      })),
    [followers],
  );

  const accountColumns = [
    {
      field: 'account',
      headerName: 'Account',
      flex: 1.2,
      minWidth: 180,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'platform',
      headerName: 'Platform',
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <PlatformChip platform={value} />,
    },
    {
      field: 'follower_count',
      headerName: 'Followers',
      type: 'number',
      flex: 0.8,
      minWidth: 120,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'report_date',
      headerName: 'Snapshot',
      flex: 0.8,
      minWidth: 110,
    },
  ];

  // ---- Content grid ----------------------------------------------------
  // Ayni sekilde: /social-metrics kimlik alani dondurmuyor. Icerik URL'i
  // benzersiz; olmadigi durumlar icin baslik + sira numarasina duseriz.
  const contentRows = useMemo(
    () =>
      (metrics.rows || []).map((r, i) => ({
        id: r.content_url || `${r.content_title || 'icerik'}|${i}`,
        ...r,
      })),
    [metrics],
  );

  const contentColumns = [
    {
      field: 'content_title',
      headerName: 'Content',
      flex: 1.6,
      minWidth: 240,
      renderCell: ({ value, row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || '(untitled)'}
          </Typography>
          {row.content_url && (
            <IconButton
              size="small"
              href={row.content_url}
              target="_blank"
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
            >
              <OpenInNewIcon fontSize="inherit" />
            </IconButton>
          )}
        </Stack>
      ),
    },
    { field: 'account', headerName: 'Account', flex: 0.9, minWidth: 140 },
    {
      field: 'platform',
      headerName: 'Platform',
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ value }) => <PlatformChip platform={value} />,
    },
    { field: 'views', headerName: 'Views', type: 'number', flex: 0.7, minWidth: 100, valueFormatter: ({ value }) => fmt(value) },
    { field: 'likes', headerName: 'Likes', type: 'number', flex: 0.6, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'comments', headerName: 'Comments', type: 'number', flex: 0.7, minWidth: 100, valueFormatter: ({ value }) => fmt(value) },
    { field: 'shares', headerName: 'Shares', type: 'number', flex: 0.6, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'impressions', headerName: 'Impressions', type: 'number', flex: 0.8, minWidth: 110, valueFormatter: ({ value }) => fmt(value) },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Social Media"
        subtitle="Latest follower counts and per-content engagement (YouTube + Instagram)."
        rightSlot={
          <Stack direction="row" spacing={1} alignItems="center">
            {(followers.asOfDate || metrics.asOfDate) && (
              <Chip
                label={`As of ${followers.asOfDate || metrics.asOfDate}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
            <MuiTooltip title="Refresh">
              <IconButton
                onClick={() => {
                  refreshCache();
                  setReloadKey((k) => k + 1);
                }}
              >
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>
          </Stack>
        }
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="accounts" label="Accounts" />
          <Tab value="content" label="Content" />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Platform</InputLabel>
          <Select label="Platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORMS.map((p) => (
              <MenuItem key={p} value={p}>
                {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {(tab === 'accounts' ? followers.warning : metrics.warning) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {tab === 'accounts' ? followers.warning : metrics.warning}
        </Alert>
      )}

      {tab === 'accounts' ? (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <MetricCard label="Accounts" value={fmt(totals.accounts)} accent={PLATFORM_COLORS.youtube} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Total followers" value={fmt(totals.followers)} accent={PLATFORM_COLORS.android} />
            </Grid>
          </Grid>

          <SectionCard
            title={`${accountRows.length} account(s)`}
            description={`Tick up to ${COMPARE_MAX} accounts to compare them.`}
          >
            {followersLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : accountRows.length === 0 ? (
              <EmptyState title="No accounts for this filter." dense />
            ) : (
              <DataTable
                rows={accountRows}
                columns={accountColumns}
                height={500}
                initialState={{
                  sorting: { sortModel: [{ field: 'follower_count', sort: 'desc' }] },
                }}
                checkboxSelection
                rowSelectionModel={accountCompareIds}
                onRowSelectionModelChange={(ids) => {
                  if (ids.length > COMPARE_MAX) {
                    setAccountCompareIds(ids.slice(-COMPARE_MAX));
                  } else {
                    setAccountCompareIds(ids);
                  }
                }}
              />
            )}
          </SectionCard>

          {accountCompareIds.length >= 2 && (
            <Box sx={{ mt: 2 }}>
              <CompareChart
                title="Compare accounts"
                description="Pick a metric to plot the ticked accounts side by side."
                rows={accountRows.filter((r) => accountCompareIds.includes(r.id))}
                labelField="account"
                metrics={[
                  { field: 'follower_count', label: 'Follower count' },
                ]}
                rightSlot={
                  <Chip
                    label={`${accountCompareIds.length} / ${COMPARE_MAX}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                }
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <MetricCard label="Contents" value={fmt(contentTotals.contents)} accent={PLATFORM_COLORS.youtube} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Total views" value={fmt(contentTotals.views)} accent={PLATFORM_COLORS.android} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Total likes" value={fmt(contentTotals.likes)} accent="#D97706" />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard label="Total comments" value={fmt(contentTotals.comments)} accent="#9333EA" />
            </Grid>
          </Grid>

          <SectionCard
            title={`${contentRows.length} item(s)`}
            description={`Tick up to ${COMPARE_MAX} contents to compare them.`}
          >
            {metricsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : contentRows.length === 0 ? (
              <EmptyState title="No content metrics for this filter." dense />
            ) : (
              <DataTable
                rows={contentRows}
                columns={contentColumns}
                height={620}
                initialState={{
                  sorting: { sortModel: [{ field: 'views', sort: 'desc' }] },
                }}
                checkboxSelection
                rowSelectionModel={contentCompareIds}
                onRowSelectionModelChange={(ids) => {
                  if (ids.length > COMPARE_MAX) {
                    setContentCompareIds(ids.slice(-COMPARE_MAX));
                  } else {
                    setContentCompareIds(ids);
                  }
                }}
              />
            )}
          </SectionCard>

          {contentCompareIds.length >= 2 && (
            <Box sx={{ mt: 2 }}>
              <CompareChart
                title="Compare contents"
                description="Pick a metric to plot the ticked contents side by side."
                rows={contentRows.filter((r) => contentCompareIds.includes(r.id))}
                labelField="content_title"
                metrics={[
                  { field: 'views',       label: 'Views' },
                  { field: 'likes',       label: 'Likes' },
                  { field: 'comments',    label: 'Comments' },
                  { field: 'shares',      label: 'Shares' },
                  { field: 'impressions', label: 'Impressions' },
                ]}
                rightSlot={
                  <Chip
                    label={`${contentCompareIds.length} / ${COMPARE_MAX}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                }
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
